import { useState } from 'react';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, CheckCircle2, MapPin, Users, Clock, BookOpen, Target, Wrench } from 'lucide-react';
import { useI18n } from './i18n';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const questions = [
  {
    id: 'savoirFaire',
    icon: Target,
    question: 'Quel savoir-faire technique souhaitez-vous travailler ?',
    type: 'single',
    options: [
      'Protection et sécurité',
      'Anticipation de crise réputationnelle',
      'Analyse du renseignement',
      'Elaboration de recommandations stratégiques pour l\'échelon décisionnaire',
    ],
  },
  {
    id: 'support',
    icon: Wrench,
    question: 'Souhaitez-vous utiliser comme support pédagogique un serious game sous format numérique ou un jeu de plateau ?',
    type: 'single',
    options: ['Serious game numérique', 'Jeu de plateau', 'Les deux'],
  },
  {
    id: 'competences',
    icon: BookOpen,
    question: 'Quelles sont les 3 compétences que vous souhaitez plus particulièrement travailler ?',
    subtitle: 'Sélectionnez 3 compétences maximum',
    type: 'multi',
    max: 3,
    options: [
      'Identifier les signaux faibles précurseurs de risques, menaces ou opportunités',
      'Prendre des décisions sous contrainte de temps avec un haut degré d\'incertitude',
      'Stimuler la vigilance, le discernement et le partage d\'information inter-fonctions',
      'Renforcer la cohésion d\'une équipe en situation de surcharge informationnelle',
      'Fiabiliser une information',
      'Maîtriser les biais cognitifs',
      'Valoriser vos entretiens professionnels et vos compétences de négociation',
    ],
  },
  {
    id: 'participants',
    icon: Users,
    question: 'Combien de personnes souhaitez-vous former par session ?',
    type: 'single',
    options: ['Moins de 10 personnes', 'Entre 10 et 20 personnes'],
  },
  {
    id: 'duree',
    icon: Clock,
    question: 'De combien de temps disposez-vous ?',
    type: 'single',
    options: ['Une demi-journée', 'Une journée', 'Entre deux et quatre jours'],
  },
  {
    id: 'theorie',
    icon: BookOpen,
    question: 'Combien de temps souhaitez-vous consacrer à l\'apport théorique ?',
    type: 'single',
    options: ['Une heure', 'Une demi-journée', 'Plus'],
  },
  {
    id: 'lieu',
    icon: MapPin,
    question: 'Où se déroulera la formation ?',
    type: 'text',
    placeholder: 'Ex : Paris, dans vos locaux, en distanciel...',
  },
];

function getRecommendations(answers) {
  const recs = [];
  const isNumeric = answers.support === 'Serious game numérique' || answers.support === 'Les deux';
  const isBoard = answers.support === 'Jeu de plateau' || answers.support === 'Les deux';

  // Based on savoir-faire
  if (answers.savoirFaire === 'Protection et sécurité') {
    if (isNumeric) recs.push({ name: 'Secure Trail', type: 'numerique', desc: 'Détecter les signaux faibles précurseurs d\'une ingérence et prendre des décisions sous forte incertitude.', duration: '1/2 journée', color: '#34B2C8' });
    if (isBoard) recs.push({ name: 'Shadow Corps', type: 'plateau', desc: 'Jeu collaboratif où les joueurs incarnent des attaquants pour mieux comprendre les mécanismes d\'ingérence.', color: '#F5A623' });
  }
  if (answers.savoirFaire === 'Anticipation de crise réputationnelle') {
    if (isNumeric) {
      recs.push({ name: 'Puno', type: 'numerique', desc: 'Produire des recommandations stratégiques et des éléments de langage face à une attaque informationnelle.', duration: '1 journée', color: '#F5A623' });
      recs.push({ name: 'Crise réputationnelle', type: 'numerique', desc: 'Identifier les signaux faibles précurseurs d\'une crise réputationnelle.', duration: '1/2 journée (suite de Puno)', color: '#E25A6E' });
    }
    if (isBoard) recs.push({ name: 'Shadow Corps', type: 'plateau', desc: 'Comprendre les mécanismes d\'attaque informationnelle à travers un jeu de plateau collaboratif.', color: '#F5A623' });
  }
  if (answers.savoirFaire === 'Analyse du renseignement') {
    if (isBoard) recs.push({ name: 'Opération HUMINT', type: 'plateau', desc: 'Jeu compétitif de négociation inspiré des opérations de renseignement humain.', color: '#34B2C8' });
    if (isNumeric) recs.push({ name: 'Puno', type: 'numerique', desc: 'Collecter des informations en équipe et produire une analyse de situation structurée.', duration: '1 journée', color: '#F5A623' });
  }
  if (answers.savoirFaire === 'Elaboration de recommandations stratégiques pour l\'échelon décisionnaire') {
    if (isNumeric) recs.push({ name: 'Puno', type: 'numerique', desc: 'Produire en équipe des recommandations stratégiques à destination de l\'échelon décisionnaire.', duration: '1 journée', color: '#F5A623' });
    if (isBoard) recs.push({ name: 'Opération HUMINT', type: 'plateau', desc: 'Développer vos compétences de négociation et de collecte d\'information stratégique.', color: '#34B2C8' });
  }

  // Fallback if no specific match
  if (recs.length === 0) {
    if (isNumeric) recs.push({ name: 'Secure Trail', type: 'numerique', desc: 'Un serious game immersif pour apprendre à détecter et réagir face aux menaces.', duration: '1/2 journée', color: '#34B2C8' });
    if (isBoard) recs.push({ name: 'Opération HUMINT', type: 'plateau', desc: 'Un jeu de société compétitif inspiré du renseignement humain.', color: '#34B2C8' });
  }

  // Deduplicate by name
  const seen = new Set();
  return recs.filter(r => { if (seen.has(r.name)) return false; seen.add(r.name); return true; });
}

export const quizQuestions = questions;

export default function QuoteBuilder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showQuote, setShowQuote] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();
  const { basePath } = useOutletContext();
  const q = t.quiz;

  const questionIds = ['savoirFaire', 'support', 'competences', 'participants', 'duree', 'theorie', 'lieu'];
  const questionTypes = ['single', 'single', 'multi', 'single', 'single', 'single', 'text'];
  const questionMaxes = [null, null, 3, null, null, null, null];
  const questionIcons = [Target, Wrench, BookOpen, Users, Clock, BookOpen, MapPin];

  const currentQ = q.questions[step];
  const currentId = questionIds[step];
  const currentType = questionTypes[step];
  const currentMax = questionMaxes[step];
  const isLast = step === q.questions.length - 1;
  const canNext =
    currentType === 'text'
      ? true
      : currentType === 'multi'
        ? (answers[currentId] || []).length > 0
        : !!answers[currentId];

  const handleSingle = (option) => {
    setAnswers(prev => ({ ...prev, [currentId]: option }));
  };

  const handleMulti = (option) => {
    const selected = answers[currentId] || [];
    if (selected.includes(option)) {
      setAnswers(prev => ({ ...prev, [currentId]: selected.filter(o => o !== option) }));
    } else if (selected.length < currentMax) {
      setAnswers(prev => ({ ...prev, [currentId]: [...selected, option] }));
    }
  };

  const handleNext = () => {
    if (isLast) {
      setShowQuote(true);
    } else {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (showQuote) {
      setShowQuote(false);
    } else if (step > 0) {
      setStep(s => s - 1);
    }
  };

  const recommendations = getRecommendations(answers);
  const Icon = questionIcons[step];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Progress bar */}
      <div className="h-1.5 bg-slate-100">
        <div
          className="h-full bg-[#34B2C8] transition-all duration-500 ease-out"
          style={{ width: showQuote ? '100%' : `${((step + 1) / q.questions.length) * 100}%` }}
        />
      </div>

      <div className="p-8 md:p-10">
        {showQuote ? (
          <motion.div key="quote" initial="hidden" animate="visible" variants={fadeUp} className="py-4">
            <CheckCircle2 size={40} className="text-[#34B2C8] mb-5" />
            <h3 className="text-2xl font-medium text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {q.resultTitle}
            </h3>
            <p className="text-sm text-slate-500 mb-8 max-w-lg">
              {q.resultSubtitle}
            </p>

            <div className="space-y-4 mb-8">
              {recommendations.map((rec, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
                >
                  <div className="w-1.5 h-full min-h-[60px] rounded-full flex-shrink-0" style={{ backgroundColor: rec.color }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1.5">
                      <h4 className="font-semibold text-slate-900">{rec.name}</h4>
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full" style={{
                        backgroundColor: rec.type === 'numerique' ? '#34B2C8' + '15' : '#F5A623' + '15',
                        color: rec.type === 'numerique' ? '#34B2C8' : '#F5A623'
                      }}>
                        {rec.type === 'numerique' ? q.seriousGame : q.boardGame}
                      </span>
                      {rec.duration && (
                        <span className="text-xs text-slate-400">{rec.duration}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed">{rec.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={handleBack}
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                {q.modifyAnswers}
              </button>
              <Link
                to={`${basePath}/scenarios`}
                className="text-sm font-medium text-[#34B2C8] hover:text-[#2a9ab0] transition-colors"
              >
                {q.viewScenarios}
              </Link>
              <button
                onClick={() => {
                  const quizData = {
                    answers,
                    recommendations: recommendations.map(r => r.name),
                    questions: q.questions.map(qn => ({ id: questionIds[q.questions.indexOf(qn)], question: qn.question })),
                  };
                  localStorage.setItem('anticiper_quiz', JSON.stringify(quizData));
                  navigate(`${basePath}/contact`);
                }}
                className="inline-flex items-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] text-white font-medium px-7 py-3 rounded-full transition-all duration-200 hover:scale-[1.03] shadow-lg shadow-[#34B2C8]/20"
              >
                {q.ctaButton}
                <ChevronRight size={16} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key={step} initial="hidden" animate="visible" variants={fadeUp}>
            {/* Question header */}
            <div className="flex items-center gap-2 text-xs font-semibold text-[#34B2C8] mb-6"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <Icon size={14} />
              QUESTION {step + 1} / {q.questions.length}
            </div>

            <h3 className="text-xl md:text-2xl font-medium text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {currentQ.question}
            </h3>
            {currentQ.subtitle && (
              <p className="text-sm text-slate-400 mb-6">{currentQ.subtitle}</p>
            )}
            {!currentQ.subtitle && <div className="mb-6" />}

            {/* Options */}
            {currentType === 'text' ? (
              <input
                type="text"
                value={answers[currentId] || ''}
                onChange={e => setAnswers(prev => ({ ...prev, [currentId]: e.target.value }))}
                placeholder={currentQ.placeholder}
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34B2C8]/30 focus:border-[#34B2C8] transition-colors"
              />
            ) : (
              <div className="space-y-3">
                {currentQ.options.map((option, i) => {
                  const isSelected = currentType === 'multi'
                    ? (answers[currentId] || []).includes(option)
                    : answers[currentId] === option;

                  return (
                    <button
                      key={i}
                      onClick={() => currentType === 'multi' ? handleMulti(option) : handleSingle(option)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className={`w-full text-left px-5 py-3.5 rounded-xl border transition-all duration-200 text-sm ${
                        isSelected
                          ? 'border-[#34B2C8] bg-[#34B2C8]/5 text-[#34B2C8] font-medium'
                          : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-${currentType === 'multi' ? 'md' : 'full'} border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'border-[#34B2C8] bg-[#34B2C8]' : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        {option}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              <button
                onClick={handleBack}
                disabled={step === 0}
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  step === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <ChevronLeft size={16} />
                {q.previous}
              </button>
              <button
                onClick={handleNext}
                disabled={!canNext}
                data-testid="quiz-next"
                className={`flex items-center gap-2 font-medium px-6 py-2.5 rounded-full transition-all duration-200 text-sm ${
                  canNext
                    ? 'bg-[#34B2C8] hover:bg-[#2a9ab0] text-white shadow-lg shadow-[#34B2C8]/20 hover:scale-[1.03]'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLast ? q.seeEstimate : q.next}
                <ChevronRight size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
